import { useState } from "react";
import { toast } from "sonner";
import { NotePencil, Trash } from "@phosphor-icons/react";

/* ============================================================================
 * NotesSection — private launch journal (add/delete per-date notes).
 * ==========================================================================*/
export function NotesSection({ notes, onCreate, onDelete }) {
  const today = new Date().toISOString().slice(0, 10);
  const [dateStr, setDateStr] = useState(today);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (text.trim().length < 2) {
      toast.error("Add a bit more detail.");
      return;
    }
    setSaving(true);
    const ok = await onCreate(dateStr, text.trim());
    setSaving(false);
    if (ok) {
      setText("");
      setDateStr(today);
    }
  };

  return (
    <section
      className="border border-white/10 bg-[#0a0a0a] p-6"
      data-testid="admin-notes"
    >
      <div className="flex items-baseline justify-between mb-5">
        <div className="flex items-center gap-3">
          <NotePencil size={18} weight="duotone" className="text-[#FF2A2A]" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A] mb-1">
              // launch journal
            </div>
            <h3 className="font-display text-xl font-extrabold tracking-tight">
              Notes
            </h3>
          </div>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
          {notes.length} entr{notes.length === 1 ? "y" : "ies"}
        </span>
      </div>

      <form onSubmit={submit} className="grid sm:grid-cols-[140px_1fr_auto] gap-3 mb-6">
        <input
          type="date"
          value={dateStr}
          onChange={(e) => setDateStr(e.target.value)}
          data-testid="note-date"
          className="bg-black border border-white/15 focus:border-[#FF2A2A] px-3 py-2 font-mono text-xs text-white focus:outline-none"
        />
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Launched IG bio link · tweet went viral · fixed dosing bug…"
          data-testid="note-text"
          className="bg-black border border-white/15 focus:border-[#FF2A2A] px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none"
        />
        <button
          type="submit"
          disabled={saving}
          data-testid="note-submit"
          className="bg-[#FF2A2A] hover:bg-[#FF4D4D] disabled:opacity-60 text-white px-5 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors"
        >
          {saving ? "Saving…" : "Add note"}
        </button>
      </form>

      {notes.length === 0 ? (
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/30">
          no notes yet · log what you tried so you can see what worked later
        </p>
      ) : (
        <ul className="space-y-2 max-h-[380px] overflow-y-auto pr-2">
          {notes.map((n) => (
            <li
              key={n.id}
              data-testid={`note-${n.id}`}
              className="group flex items-start gap-4 border-l-2 border-white/10 pl-4 py-2 hover:border-[#FF2A2A]/60 transition-colors"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF2A2A] w-20 shrink-0 pt-0.5">
                {n.date}
              </div>
              <div className="flex-1 text-sm text-white/85 leading-relaxed whitespace-pre-wrap">
                {n.text}
              </div>
              <button
                onClick={() => onDelete(n.id)}
                data-testid={`note-delete-${n.id}`}
                aria-label="Delete note"
                className="opacity-0 group-hover:opacity-100 text-white/40 hover:text-[#FF2A2A] transition-opacity"
              >
                <Trash size={14} weight="bold" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
